// =========================================================================
// Row projections for the module hub's three tables, plus the agent
// "last run" context.
//
// Split out of `ModuleScene.transform.ts` because these four take loaded data
// rather than only the seed: the open-job table, the who-pays table that only
// `commercial` renders, the service-promise table that only `promise`
// renders, and the fourteen figures the agent roster's "last run" sentences
// interpolate.
//
// Transformation layer — pure functions of explicit arguments. No React,
// no fetching, no side effects.
// Fork-owned (zone C), P5a lane.
// =========================================================================

import { EXCHANGE, fmt$ } from '@/versions/v1/shared/oem-engine';
import type { OemJob, PayerVerdict, ServicePromise } from '@/versions/v1/shared/oem-engine';
import type { AgentLastRunContext } from '@/versions/v1/shared/seed/agents';
import type { SeedNav } from '@/versions/v1/shared/seed/modules';
import { jobNav, jobRunHref } from '@/versions/v1/modules/jobs/job-nav';
import type { WorkOrder } from '@/versions/v1/schema/work-order';
import type { MeasureTone } from '@/versions/v1/shared/oem-tiles';
import { IDS } from '@/versions/v1/data/core';
import type { FigureContext } from './live-figure';

/** A row of "In this module now". */
export interface OpenJobRow {
  /**
   * Stable React key.
   *
   * `id` is **not** unique on this table: a machine can carry an alert and a
   * work order that both print the same id stem, and `wo_id` repeats across
   * the ledger. The key is the id, the kind and the row's position, which is
   * unique by construction.
   */
  readonly key: string;
  readonly id: string;
  readonly kind: string;
  readonly title: string;
  readonly machine: string | null;
  readonly mine: string;
  readonly next: string;
  readonly owner: string;
  readonly pct: number;
  readonly tone: MeasureTone;
  /** Where the row opens — the demo's `jobs()[i].open()`. */
  readonly nav: SeedNav;
  /** The row's **run** link — `show('jobrun', j.run || 'job:' + j.id)`. */
  readonly runHref: string;
}

/** Severity → pill tone, the demo's ladder at line 3021. */
const jobTone = (sev: string): MeasureTone =>
  sev === 'crit' ? 'bad' : sev === 'done' ? 'good' : sev === 'info' ? 'flat' : 'warn';

/** The open jobs sitting at this module — demo line 3011's `jobs().filter(...)`. */
export function openJobRows(
  moduleId: string,
  jobs: readonly { readonly stage: string }[],
): readonly OpenJobRow[] {
  return (jobs as readonly OemJob[])
    .filter((j) => j.stage === moduleId)
    .map(
      (j, i): OpenJobRow => ({
        key: `${j.id}|${j.kind}|${i}`,
        id: j.id,
        kind: j.kind,
        title: j.title,
        machine: j.machine,
        mine: j.mine,
        next: j.next,
        owner: j.owner,
        pct: j.pct,
        tone: jobTone(j.sev),
        nav: jobNav(j),
        runHref: jobRunHref(j),
      }),
    );
}

/** A row of "Who pays · every open job with a machine". */
export interface PayerRow {
  readonly key: string;
  readonly id: string;
  readonly kind: string;
  readonly machine: string;
  readonly mine: string;
  /** Contract type, first clause only — the demo's `.split(' — ')[0]`. */
  readonly contract: string;
  /** `'Active to 2027-03-14'` or `'Expired'`. */
  readonly warranty: string;
  readonly payer: string;
  readonly payerTone: MeasureTone;
  /** The draft work order's value, or `'—'` when there is no draft. */
  readonly value: string;
}

/** The Maximo fields the who-pays table reads. */
interface AssetContractFields {
  readonly warranty?: { readonly status?: string; readonly end?: string };
  readonly servicecontract?: { readonly type?: string };
}

/** The demo's five-way payer tone at line 3016, mapped onto the tile tones. */
const payerTone = (tone: string): MeasureTone =>
  tone === 'good' ? 'good' : tone === 'crit' ? 'bad' : tone === 'info' ? 'flat' : 'warn';

/**
 * Who carries the cost of every open job that names a machine — demo line 3016.
 *
 * `sev !== 'done'` drops the rows that have already closed, as the demo did.
 */
export function payerRows(
  jobs: readonly { readonly stage: string }[],
  workOrders: readonly { readonly machine_id: string; readonly status: string }[],
  payFor: (machineId: string) => PayerVerdict,
  // Structural, not the schema interface: the caller hands over the workflow
  // context's `mxOf`, which is narrowed to the fields the seed names. These
  // four are all this table prints.
  mxOf: (machineId: string) => AssetContractFields | undefined,
): readonly PayerRow[] {
  return (jobs as readonly OemJob[])
    // The context narrows `machine` from `string | null` to `string |
    // undefined` on the way in, so the guard has to accept both spellings of
    // "this row is fleet-level and names no machine".
    .filter((j) => Boolean(j.machine) && j.sev !== 'done')
    .map((j, i): PayerRow => {
      const machine = j.machine as string;
      const mx = mxOf(machine);
      const draft = (workOrders as readonly WorkOrder[]).find(
        (w) => w.machine_id === machine && w.status.startsWith('Draft'),
      );
      const verdict = payFor(machine);
      const warranty = mx?.warranty;

      return {
        key: `${j.id}|${machine}|${i}`,
        id: j.id,
        kind: j.kind,
        machine,
        mine: j.mine,
        contract: mx?.servicecontract?.type?.split(' — ')[0] ?? '—',
        warranty: warranty
          ? `${warranty.status}${warranty.status === 'Active' && warranty.end ? ` to ${warranty.end}` : ''}`
          : '—',
        payer: verdict.label,
        payerTone: payerTone(verdict.tone),
        value: draft ? fmt$(draft.total_direct_cost_aud) : '—',
      };
    });
}

/** A row of "Service promises · every open alert". */
export interface PromiseRow {
  readonly key: string;
  readonly id: string;
  readonly machine: string;
  readonly mine: string;
  readonly failureWindow: string;
  readonly parts: string;
  readonly tech: string;
  readonly window: string;
  readonly fix: string;
  readonly inside: boolean;
}

/** Every alert with a service window — demo line 3016's second table. */
export function promiseRows(
  promises: readonly { readonly inside: boolean }[],
): readonly PromiseRow[] {
  return (promises as readonly ServicePromise[]).map(
    (p): PromiseRow => ({
      key: p.id,
      id: p.id,
      machine: p.machine,
      mine: p.mine,
      failureWindow: p.win.label,
      parts: p.win.parts,
      tech: p.win.tech,
      window: p.win.window,
      fix: p.win.fix,
      inside: p.inside,
    }),
  );
}

/**
 * The fourteen figures the agent roster's "last run" sentences interpolate.
 *
 * `agents.ts` models each one as either a literal or a token over this
 * context, so the roster never reaches for a global. Assembled from the
 * figure context, which already has every input.
 */
export function lastRunContext(figures: FigureContext): AgentLastRunContext {
  const { step } = figures;
  const mx = step.pack.mx as Readonly<Record<string, { children?: unknown[] }>>;

  return {
    openAlerts: step.alerts.length,
    openCases: step.pack.cases.filter((c) => /Open/.test(c.status)).length,
    draftWorkOrders: step.workOrders.filter((w) => w.status.startsWith('Draft')).length,
    agentRequisitions: step.pack.orders.filter((o) => /agent/i.test(o.status)).length,
    jobsInProgress: step.pack.tapp_jobs.filter((j) => j.status === 'In progress').length,
    fleetRules: step.fleetRules.length,
    assetRecords: Object.keys(mx).length,
    serialisedSubAssets: Object.values(mx).reduce(
      (a, m) => a + (Array.isArray(m?.children) ? m.children.length : 0),
      0,
    ),
    flaggedComponents: step.health.filter((h) => ['major', 'vpoor', 'poor'].includes(h.band))
      .length,
    pmsScheduledAhead: step.workOrders.filter(
      (w) =>
        w.wo_type === 'Preventive' && w.status === 'Scheduled' && w.created > '2026-09-09',
    ).length,
    minimumLevelProposals: step.derived.predictedUsage.length,
    promises: {
      total: figures.promises.length,
      inside: figures.promises.filter((p) => p.inside).length,
    },
    exchangeUnits: EXCHANGE.length,
    contracts: step.pack.mx_contracts.length,
    upgradeCandidates: step.derived.upgradeCandidates.length,
    // `renewals()` is sorted soonest-first by the engine, so the head is the
    // next one due. An empty contract list has no next renewal; `0` reads as
    // "due now", which is the safer of the two wrong answers to show, and
    // the shipped pack always has six.
    nextRenewalDays: step.derived.renewals[0]?.days ?? 0,
    machineIds: IDS as AgentLastRunContext['machineIds'],
  };
}
